import AccountTreeOutlined from "@mui/icons-material/AccountTreeOutlined";
import CancelRounded from "@mui/icons-material/CancelRounded";
import DownloadOutlined from "@mui/icons-material/DownloadOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import FileUploadOutlined from "@mui/icons-material/FileUploadOutlined";
import SaveOutlined from "@mui/icons-material/SaveOutlined";
import ViewListOutlined from "@mui/icons-material/ViewListOutlined";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

import { blue, green, grey, orange, red } from "@mui/material/colors";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import size from "lodash/size";
import PropTypes from "prop-types";
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  AlarmOutlined,
  AssignmentOutlined,
  AssuredWorkloadOutlined,
  CategoryOutlined,
  CheckBoxOutlineBlank,
  CheckBoxOutlined,
  CheckCircleOutline,
  ChecklistOutlined,
  CloseOutlined,
  ContentPasteSearchOutlined,
  CoPresent,
  HighlightAltOutlined,
  ImageSearchOutlined,
  OutboxOutlined,
  PageviewOutlined,
  PublishedWithChangesOutlined,
  ReplyOutlined,
  RuleOutlined,
  SchemaOutlined,
  SortByAlphaOutlined,
  SortRounded,
  SwapVertOutlined,
  TocOutlined,
  UploadFileOutlined,
  WarningAmberRounded,
} from "@mui/icons-material";
import { CircularProgress, Divider, IconButton, Tooltip } from "@mui/material";
import { isEqual } from "lodash";
import FlxGrid from "../../../../_src_shared/components/layout/FlxGrid";
import FileUploader from "../../../../components/common/FileUploader";
import LoanFileCommentsBadge from "../../../../components/common/Loan/LoanFileCommentsBadge";
import SearchInput from "../../../../components/inputs/SearchInput";
import SelectToggleInput from "../../../../components/inputs/SelectToggleInput";
import InputWrapper from "../../../../components/inputs/shared/InputWrapper";
import Flx from "../../../../components/layout/Flx";
import ScreenContent from "../../../../components/layout/ScreenContent";
import TitledHeaderWithSearch from "../../../../components/layout/TitledHeaderWithSearch";
import LoadingFallback from "../../../../components/LoadingFallback";
import PinnedModal from "../../../../components/modals/PinnedModal";
import Txt from "../../../../components/typography/Txt";
import SubsectionHeader from "../../../../components/ui/SubsectionHeader";
import TableSortDropdown from "../../../../components/ui/TableSortDropdown";
import { LOAN_FILES_TABLE_LAYOUT_TYPES } from "../../../../constants/enums/LOAN_FILES_TABLE_LAYOUT_TYPES";
import { useLoanFilesHook } from "../../../../hooks/useLoanFilesHook";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";
import { sidebarSetValues } from "../../../../redux/actions/sidebarActions";
import LoanBulkFileUploader from "../../shared/LoanBulkFileUploader";
import LoanDrilldownTabPanel from "../LoanDrilldownTabPanel";
import LoanExpiringDocumentsRenderer from "./LoanExpiringDocumentsRenderer";
import LoanFilesTable from "./LoanFilesTable/LoanFilesTable";
import LoanRequiredDocsPanel from "./LoanRequiredDocsPanel";
import DropdownButton from "../../../../components/ui/buttons/DropdownButton";
import UnsavedChangesAlert from "../../../../components/common/UnsavedChangesAlert";
import LoanParsedDocsPanel from "./LoanRequiredDocsPanel/LoanParsedDocsPanel";
import { useReduxHook } from "../../../../hooks/useReduxHook";
import { loanFetchedDataParsedDocsClear } from "../../../../redux/reducers/loanFetchedDataReducer";
import { useEnv } from "../../../../hooks/useEnv";
const LOAN_FILE_ACTION_TYPES = {
  SEND_BANK_STATEMENTS_TO_OCRULUS: {
    label: "Send Bank Statements to Ocrulus",
    // icon: <UploadFileOutlined className="thin" />,
    icon: <AssuredWorkloadOutlined className="thin" />,
    name: "SEND_BANK_STATEMENTS_TO_OCRULUS",
    submitButtonText: "Send",
  },
  PARSE_AND_EXTRACT_DATA_FROM_DOCUMENTS: {
    label: "Extract Data from Documents",
    // icon: <SchemaOutlined className="thin" />,
    // icon: <HighlightAltOutlined className="thin" />,
    icon: <OutboxOutlined className="thin" />,
    name: "PARSE_AND_EXTRACT_DATA_FROM_DOCUMENTS",
  },
};

const LoanDrilldownFilesTab = ({ value, tab }) => {
  // Derived Stateo
  const loanDrilldown = useSelector((state) => state?.loanDrilldown);
  const loanDocuments = useMemo(
    () => loanDrilldown?.loanDocuments || [],
    [loanDrilldown],
  );

  // State
  // const [layoutMode, setLayoutMode] = useState("all"); // "all" ||  "group"  ||  "hidden" || "status"
  const [layoutMode, setLayoutMode] = useState(
    LOAN_FILES_TABLE_LAYOUT_TYPES.LIST,
    // LOAN_FILES_TABLE_LAYOUT_TYPES.LIST
  ); // "all" ||  "group"  ||  "hidden" || "status"
  const [showUpload, setShowUpload] = useState(false);

  // Filter documents based on showHiddenFiles setting
  const filteredDocuments = useMemo(() => {
    // if (layoutMode === "hidden") {
    if (layoutMode === LOAN_FILES_TABLE_LAYOUT_TYPES.HIDDEN) {
      return loanDocuments.filter((doc) => doc.isHidden);
    }
    return loanDocuments.filter((doc) => !doc.isHidden);
  }, [loanDocuments, layoutMode]);

  // Calculate counts for display
  const hiddenCount = useMemo(() => {
    return loanDocuments.filter((doc) => doc.isHidden).length;
  }, [loanDocuments]);

  const visibleCount = useMemo(() => {
    return loanDocuments.filter((doc) => !doc.isHidden).length;
  }, [loanDocuments]);

  const tableHeaderTitle = useMemo(() => {
    return `Loan Files (${visibleCount})`;
  }, [visibleCount]);

  return (
    <LoanDrilldownTabPanel value={value} tab={tab}>
      <TitledHeaderWithSearch
        title={tableHeaderTitle}
        endContent={
          <Flx gap={1} sx={{ position: "absolute", right: 24 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setShowUpload(true)}
              startIcon={<FileUploadOutlined className="thin" />}
            >
              Upload By Category
            </Button>
            {/* <GetAuditLogsButton /> */}
            <Suspense fallback={<LoadingFallback />}>
              <LoanBulkFileUploader
                defaultGroupUpload
                show={showUpload}
                onClose={() => setShowUpload(false)}
              />
            </Suspense>
          </Flx>
        }
      >
        <Suspense fallback={<LoadingFallback />}>
          <FileUploader
            suppressUploadButton
            height={"80px"}
            borderSize={2}
            sx={{ mt: 2 }}
          />
        </Suspense>
      </TitledHeaderWithSearch>
      <ScreenContent padding={0}>
        <LoanExpiringDocumentsRenderer documents={filteredDocuments} />
        <LoanFilesTableContent
          files={filteredDocuments}
          layoutMode={layoutMode}
          setLayoutMode={setLayoutMode}
          hiddenCount={hiddenCount}
        />
      </ScreenContent>
    </LoanDrilldownTabPanel>
  );
};

const LoanFilesTableContent = ({
  files,
  hiddenCount,
  layoutMode,
  setLayoutMode,
}) => {
  const [freezePanes, setFreezePanes] = useState(false);
  const gridRef = useRef(null);
  const dispatch = useDispatch();
  const [sortFilter, setSortFilter] = useState({
    colId: "docGroup",
    sort: "asc",
  });
  const { loading, updateLoanDocMetadata } = useLoanFilesHook();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  // Local state for the grid's row data
  const [rowData, setRowData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [changedRows, setChangedRows] = useState([]);

  // More local state
  const [quickFilter, setQuickFilter] = useState(null);
  const [editing, setEditing] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [actioning, setActioning] = useState(false);

  // Config
  const changedRowsCount = useMemo(() => size(changedRows), [changedRows]);

  // Functions
  const handleLayoutModeChange = useCallback(
    (event, newMode) => {
      if (newMode !== null) {
        setLayoutMode(newMode);
      }
    },
    [setLayoutMode],
  );

  const onSortFilterChange = useCallback(
    (fil) => {
      if (isEqual(fil, sortFilter)) {
        setSortFilter(null); // Reset to no sorting
        return;
      }
      setSortFilter(fil);
    },
    [sortFilter],
  );

  const onActioningClick = useCallback((action) => {
    setAssigning(false);
    setActioning(action);
    setEditing(false);
  }, []);

  const onEditClick = useCallback(() => {
    setAssigning(false);
    setActioning(false);
    setEditing(true);
  }, []);

  const onAssignClick = useCallback(() => {
    setActioning(false);
    setAssigning(true);
    setEditing(true);
  }, []);

  const onEditCancel = useCallback(() => {
    setRowData(files.map((f) => ({ ...f })));
    setSelectedRows([]);
    setChangedRows([]);
    setActioning(false);
    setEditing(false);
    setAssigning(false);
  }, [files]);

  const onUpdateClick = useCallback(() => {
    updateLoanDocMetadata({
      loanId: loanDrilldown?._id,
      data: changedRows,
      onSuccessFn: (data) => {
        dispatch(loanDrilldownSet(data));
      },
    });
  }, [updateLoanDocMetadata, loanDrilldown?._id, changedRows, dispatch]);

  const styles = useMemo(() => {
    const baseStyles = {
      mt: 3,
      p: 2,
    };

    return baseStyles;
  }, []);

  useEffect(() => {
    // Copy "files" into our local state so we can edit
    setRowData(files.map((f) => ({ ...f })));
    // Clearing any prior changedRows if "files" has changed
    setSelectedRows([]);
    setChangedRows([]);
    setActioning(false);
    setEditing(false);
    setAssigning(false);
  }, [files]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (changedRowsCount > 0) {
        // Chrome requires returnValue to be set
        event.preventDefault();
        event.returnValue = "";
        // returnValue = '' is needed for most browsers to show the native prompt
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [changedRowsCount]);

  return (
    <Flx column gap={0} sx={styles}>
      <LayoutToggle
        editing={editing}
        value={layoutMode}
        onChange={handleLayoutModeChange}
        hiddenCount={hiddenCount}
      />

      {isEmpty(selectedRows) || !assigning ? null : (
        <AssignDocumentModal selected={selectedRows} onClose={onEditCancel} />
      )}

      {actioning ? (
        <LoanFilesActionBanner
          actionType={actioning}
          selected={selectedRows}
          onCancel={onEditCancel}
        />
      ) : null}
      {/* {!isEmpty(selectedRows) && actioning ? (
        <LoanFilesActionBanner
          actionType={actioning}
          selected={selectedRows}
          onCancel={onEditCancel}
        />
      ) : null} */}
      <Card
        // variant="outline"
        sx={{
          borderTopLeftRadius: 0,
          p: 0,
          boxShadow: "none",
          border: `1px solid ${grey[300]}`,
          // overflow: "visible",
          // overflow: "auto",
        }}
      >
        <Suspense fallback={<LoadingFallback />}>
          {layoutMode === LOAN_FILES_TABLE_LAYOUT_TYPES.CHECKLIST ||
          layoutMode === LOAN_FILES_TABLE_LAYOUT_TYPES.PARSED_AND_EXTRACTED ? (
            <>
              {layoutMode === LOAN_FILES_TABLE_LAYOUT_TYPES.CHECKLIST && (
                <LoanRequiredDocsPanel files={rowData} />
              )}
              {layoutMode ===
                LOAN_FILES_TABLE_LAYOUT_TYPES.PARSED_AND_EXTRACTED && (
                <LoanParsedDocsPanel files={rowData} />
              )}
            </>
          ) : (
            <>
              <FileTableCardHeader
                quickFilter={quickFilter}
                setQuickFilter={setQuickFilter}
                // Layout

                onLayoutChange={handleLayoutModeChange}
                // Hidden Files
                hiddenCount={hiddenCount}
                freezePanes={freezePanes}
                assigning={assigning}
                // assigning={assigning || actioning}
                setFreezePanes={setFreezePanes}
                // Editing
                editing={editing}
                actioning={actioning}
                onEditCancel={onEditCancel}
                onEditClick={onEditClick}
                onAssignClick={onAssignClick}
                onActioningClick={onActioningClick}
                // Updating
                changedRows={changedRows}
                onUpdateClick={onUpdateClick}
                changedRowsCount={changedRowsCount}
                sortFilter={sortFilter}
                setSortFilter={setSortFilter}
                loading={loading}
              />
              <LoanFilesTable
                ref={gridRef}
                rowData={rowData}
                setRowData={setRowData}
                layoutMode={layoutMode}
                // groupByCategory={groupByCategory}
                // groupByStatus={groupByStatus}
                editing={editing}
                // assigning={assigning}
                selectable={assigning || actioning}
                assigning={assigning || actioning}
                freezePanes={freezePanes}
                changedRows={changedRows}
                setChangedRows={setChangedRows}
                quickFilter={quickFilter}
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
                sortFilter={sortFilter}
                onEditClick={onEditClick}
                setSortFilter={onSortFilterChange}
              />
            </>
          )}
        </Suspense>
      </Card>
    </Flx>
  );
};

const AssignDocumentModal = ({ selected, onClose }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);

  const [assignmentType, setAssignmentType] = useState(null);
  const [associationId, setAssociationId] = useState(null);
  const dispatch = useDispatch();
  const { loading, associateDocumentWithObjects } = useLoanFilesHook();

  const assignmentTypeOptions = useMemo(
    () => [
      { label: "Borrower", value: "borrowers" },
      { label: "Property", value: "subjectProperties" },
      { label: "Entity", value: "borrowerEntity" },
      // { label: "Entity", value: "entities" },
    ],
    [],
  );

  const onAssignmentTypeChange = useCallback((v) => {
    setAssignmentType(v);
    setAssociationId(null);
  }, []);

  const associationOptions = useMemo(() => {
    if (assignmentType === "borrowerEntity") {
      return [
        {
          label: loanDrilldown?.borrowerEntity?.entityName || "Unnamed Entity",
          value: loanDrilldown?.borrowerEntity?._id,
        },
      ];
    }
    const targetList = loanDrilldown?.[assignmentType] || [];

    return targetList?.map((b) => {
      let label = "Unnamed";
      if (assignmentType === "borrowers") {
        label =
          `${b?.firstName || ""} ${b?.lastName || ""}`.trim() || "Unnamed ";
      }
      if (assignmentType === "subjectProperties") {
        label = b?.address?.fullAddress || "Unnamed";
      }
      if (assignmentType === "entities") {
        label = b?.entityName || "Unnamed";
      }
      // if (assignmentType === "entities") {
      //   label = b?.address?.fullAddress || "Unnamed";
      // }
      return {
        label,
        value: b._id,
      };
    });
  }, [loanDrilldown, assignmentType]);

  const associationLabel = useMemo(() => {
    if (assignmentType === "borrowers") {
      return "Select Borrower:";
    }
    if (assignmentType === "subjectProperties") {
      return "Select Property:";
    }
    if (assignmentType === "entities" || assignmentType === "borrowerEntity") {
      return "Select Entity:";
    }
    return "Invalid";
  }, [assignmentType]);

  const onSubmit = useCallback(() => {
    const selectedIds = selected.map((s) => s._id);
    associateDocumentWithObjects({
      dealId: loanDrilldown?._id,
      loanDocumentsIds: selectedIds,
      payload: {
        [`${assignmentType === "borrowerEntity" ? "entities" : assignmentType}`]:
          [associationId],
      },
      onSuccessFn: (d) => {
        dispatch(loanDrilldownSet(d));
        onClose();
      },
    });
  }, [
    loanDrilldown?._id,
    assignmentType,
    associationId,
    selected,
    associateDocumentWithObjects,
  ]);
  return (
    <PinnedModal
      show={true}
      title={
        size(selected) > 1
          ? `Assign (${size(selected)}) Documents:`
          : `Assign (${size(selected)}) Document:`
      }
      height="320px"
      icon={<CheckCircleOutline className="thin" />}
      maxWidth="480px"
      onClose={onClose}
    >
      <Flx column jb gap={4} sx={{ flexGrow: 1, p: 2, pb: 0 }}>
        <Flx column jb gap={4}>
          <InputWrapper
            label={`Assign the following ${size(selected) > 1 ? "documents" : "document"} to a:`}
          >
            <SelectToggleInput
              options={assignmentTypeOptions}
              value={assignmentType}
              onChange={onAssignmentTypeChange}
            />
          </InputWrapper>
          {!isNil(assignmentType) && (
            <InputWrapper label={associationLabel}>
              <SelectToggleInput
                value={associationId}
                onChange={setAssociationId}
                options={associationOptions}
              />
            </InputWrapper>
          )}
        </Flx>
        <Flx fw end gap={1}>
          <Button
            size="large"
            color="error"
            variant={"outlined"}
            onClick={onClose}
          >
            Cancel
          </Button>

          <Button
            loading={loading}
            disabled={isNil(associationId)}
            // variant={primaryButtonVariant}
            size="large"
            // color="success"
            loadingPosition="end"
            onClick={onSubmit}
          >
            Save & Assign
          </Button>
        </Flx>
      </Flx>
    </PinnedModal>
  );
};

const FileTableCardHeader = ({
  quickFilter,
  setQuickFilter,

  onUpdateClick,
  loading,
  assigning,
  actioning,
  //Display values
  changedRowsCount,
  freezePanes,
  setFreezePanes,
  //Editing
  editing,
  onEditCancel,
  sortFilter,
  setSortFilter,
  onEditClick,
  onAssignClick,
  onActioningClick,
}) => {
  const rootStyles = useMemo(() => {
    return {
      p: 1,
      borderBottom: `1px solid ${grey[300]}`,
      display: "flex",
      alignItems: "center",
      gap: 2,
      flexWrap: "wrap",
      position: "sticky",
      top: 0,
      left: 0,
    };
  }, []);

  return (
    <Box sx={rootStyles}>
      <Flx fw gap={1} ac wrap sx={{ flex: 1, minWidth: 250 }}>
        <SearchInput value={quickFilter} onChange={setQuickFilter} />

        <Flx ac gap={2} sx={{ ml: 1 }}>
          <Flx ac gap={0.6}>
            <Flx ac gap={0.4}>
              <SortRounded sx={{ fontSize: "15px", opacity: 0.5 }} />
              <Txt sx={{ fontSize: "11px" }} secondary>
                Sort By
              </Txt>
            </Flx>

            <TableSortDropdrown
              selectedSort={sortFilter}
              onSortChange={setSortFilter}
            />
          </Flx>
          <Flx ac gap={0.3}>
            <Button
              size="small"
              variant="text"
              color="inherit"
              sx={{
                fontSize: "11px",
                // fontWeight: freezePanes ? 500 : 400,
                fontWeight: 400,
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                px: 1,
                py: 0.6,
                // backgroundColor: freezePanes ? blue[50] : "transparent",
                color: freezePanes ? "#2962ff" : "rgba(0, 0, 0, 0.6)",
                ".MuiButton-icon": {
                  mr: "4px",
                },
              }}
              startIcon={
                freezePanes ? (
                  <CheckBoxOutlined
                    className="thin"
                    sx={{ fontSize: "16px !important", color: "#2962ff" }}
                  />
                ) : (
                  <CheckBoxOutlineBlank
                    className="thin"
                    sx={{ fontSize: "16px !important", color: grey[400] }}
                  />
                )
              }
              onClick={() => setFreezePanes((s) => !s)}
            >
              Freeze Panes
            </Button>
          </Flx>
        </Flx>
      </Flx>

      <Flx
        gap={1}
        ac
        sx={{
          ".MuiButtonBase-root": {
            fontWeight: 400,
            fontSize: "11px",
            ".MuiSvgIcon-root": {
              fontSize: "16px",
            },
          },
        }}
      >
        <TableActionButtons
          editing={editing}
          actioning={actioning}
          assigning={assigning}
          onEditCancel={onEditCancel}
          onEditClick={onEditClick}
          onUpdateClick={onUpdateClick}
          onAssignClick={onAssignClick}
          onActioningClick={onActioningClick}
          loading={loading}
          changedRowsCount={changedRowsCount}
        />
      </Flx>
    </Box>
  );
};

const TableActionButtons = ({
  editing,
  assigning,
  onEditCancel,
  onUpdateClick,
  loading,
  onAssignClick,
  onActioningClick,
  onEditClick,
  actioning,
  changedRowsCount,
}) => {
  if (assigning) {
    return (
      <CancelButton
        onClick={onEditCancel}
        startIcon={
          <AssignmentOutlined
            className="thin-9"
            sx={{ fontSize: "16px !important" }}
          />
        }
      >
        Cancel Assigning
      </CancelButton>
    );
  }

  if (actioning) {
    return (
      <CancelButton
        onClick={onEditCancel}
        startIcon={
          <CloseOutlined
            className="thin-6"
            sx={{ fontSize: "16px !important" }}
          />
        }
      >
        Cancel Actioning
      </CancelButton>
    );
  }

  if (editing) {
    return (
      <>
        <Button
          startIcon={<CancelRounded className="thin-7" />}
          variant="outlined"
          color="error"
          size="medium"
          onClick={onEditCancel}
          sx={{ fontWeight: 400, fontSize: "11px" }}
        >
          Cancel Editing
        </Button>
        <Button
          loading={loading}
          size="medium"
          disabled={changedRowsCount < 1}
          onClick={onUpdateClick}
          color="success"
          sx={{ fontWeight: 400, fontSize: "11px", background: green[700] }}
          startIcon={<SaveOutlined />}
        >
          Save Changes ({changedRowsCount})
        </Button>
      </>
    );
  }

  return (
    <>
      <ActionLoanFilesButton onClick={onActioningClick} />
      <Button
        variant="outlined"
        size="medium"
        onClick={onAssignClick}
        startIcon={
          <AssignmentOutlined
            className="thin-9"
            sx={{ fontSize: "16px !important" }}
          />
        }
        sx={{ fontWeight: 400, fontSize: "11px" }}
      >
        Assign Files
      </Button>
      <Button
        variant="contained"
        size="medium"
        onClick={onEditClick}
        startIcon={
          <EditOutlined
            className="thin-9"
            sx={{ fontSize: "16px !important" }}
          />
        }
        sx={{ fontWeight: 400, fontSize: "11px" }}
      >
        Edit Files
      </Button>
      <Divider orientation="vertical" flexItem sx={{ ml: 1 }} />
      <DownloadLoanFilesAsZipButton />
    </>
  );
};

const ActionLoanFilesButton = ({ onClick }) => {
  const { isLendmarq } = useEnv();
  // const { isTempDevUser } = useReduxHook();
  const isSmallScreen = useMemo(() => {
    return true;
    return window.innerWidth < 1200;
  }, []);
  const options = useMemo(
    () => [
      {
        label: LOAN_FILE_ACTION_TYPES.SEND_BANK_STATEMENTS_TO_OCRULUS.label,
        onClick: () => {
          onClick(LOAN_FILE_ACTION_TYPES.SEND_BANK_STATEMENTS_TO_OCRULUS.name);
        },
        icon: LOAN_FILE_ACTION_TYPES.SEND_BANK_STATEMENTS_TO_OCRULUS.icon,
        // icon: <AssuredWorkloadOutlined className="thin" />,
      },

      {
        label:
          LOAN_FILE_ACTION_TYPES.PARSE_AND_EXTRACT_DATA_FROM_DOCUMENTS.label,
        onClick: () => {
          onClick(
            LOAN_FILE_ACTION_TYPES.PARSE_AND_EXTRACT_DATA_FROM_DOCUMENTS.name,
          );
        },
        icon: LOAN_FILE_ACTION_TYPES.PARSE_AND_EXTRACT_DATA_FROM_DOCUMENTS.icon,
      },
    ],
    [onClick],
  );
  if (!isLendmarq) {
    return null;
  }

  if (isSmallScreen) {
    return (
      <>
        {options.map((op) => {
          return (
            <Button
              variant="outlined"
              // variant="contained"
              // size="small"
              size="medium"
              sx={{ fontWeight: 400, fontSize: "11px" }}
              startIcon={op.icon}
              onClick={op.onClick}
            >
              {op.label}
            </Button>
          );
        })}
      </>
    );
  }

  return (
    <DropdownButton
      variant="contained"
      size="medium"
      options={options}
      sx={{ fontWeight: 400, fontSize: "11px" }}
      startIcon={
        <ReplyOutlined
          className="thin-9"
          sx={{ fontSize: "16px !important" }}
        />
      }
    >
      Action Files
    </DropdownButton>
  );
};

const CancelButton = ({ onClick, startIcon, children }) => {
  return (
    <Button
      variant="outlined"
      color="error"
      size="medium"
      onClick={onClick}
      startIcon={startIcon}
      sx={{ fontWeight: 400, fontSize: "11px" }}
    >
      {children}
    </Button>
  );
};

const LayoutToggle = ({ value, onChange, editing, hiddenCount }) => {
  return (
    <Flx
      sx={{
        ml: 0,
        overflowX: "auto",
        flexGrow: 0,
        border: `1px solid #dde1e7`,
        borderBottom: "none",
        width: "fit-content",
        borderTopLeftRadius: "8px",
        borderTopRightRadius: "8px",
        ".MuiButtonBase-root": {
          borderBottom: "none",
          border: "none",
          borderBottomRightRadius: 0,
          flexShrink: 0,
        },
      }}
    >
      <ToggleButtonGroup
        value={value}
        exclusive
        disabled={editing}
        onChange={onChange}
        size="small"
        sx={{
          "& .MuiToggleButton-root": {
            borderBottomLeftRadius: 0,
            background: "#ffffff",
            textTransform: "none",
            fontSize: "11px",
            px: 2,
            py: 1.5,
            // px: 1.5,
          },

          "& .Mui-selected": {
            background: `${blue[50]} !important`,
          },
        }}
      >
        <ToggleButton value={LOAN_FILES_TABLE_LAYOUT_TYPES.LIST}>
          <ViewListOutlined
            className="thin"
            sx={{ fontSize: "16px", mr: 0.5 }}
          />
          All Files
          {/* Files List */}
        </ToggleButton>
        <ToggleButton value={LOAN_FILES_TABLE_LAYOUT_TYPES.CATEGORY_AND_TYPE}>
          <AccountTreeOutlined
            className="thin"
            sx={{ fontSize: "16px", mr: 0.5 }}
          />
          By Category & Type
        </ToggleButton>

        <ToggleButton value={LOAN_FILES_TABLE_LAYOUT_TYPES.CATEGORY}>
          <CategoryOutlined
            className="thin"
            sx={{ fontSize: "16px", mr: 0.5 }}
          />
          By Category
        </ToggleButton>

        <ToggleButton value={LOAN_FILES_TABLE_LAYOUT_TYPES.TYPE}>
          <SchemaOutlined className="thin" sx={{ fontSize: "16px", mr: 0.5 }} />
          By Type
        </ToggleButton>
        <ToggleButton value={LOAN_FILES_TABLE_LAYOUT_TYPES.STATUS}>
          <RuleOutlined className="thin" sx={{ fontSize: "16px", mr: 0.5 }} />
          By Status
        </ToggleButton>
        <ToggleButton value={LOAN_FILES_TABLE_LAYOUT_TYPES.CHECKLIST}>
          <ChecklistOutlined
            className="thin"
            sx={{ fontSize: "16px", mr: 0.5 }}
          />
          Checklist
        </ToggleButton>
        <ToggleButton
          value={LOAN_FILES_TABLE_LAYOUT_TYPES.PARSED_AND_EXTRACTED}
        >
          <ImageSearchOutlined
            className="thin"
            sx={{ fontSize: "16px", mr: 0.5 }}
          />
          {/* <ContentPasteSearchOutlined
            className="thin"
            sx={{ fontSize: "16px", mr: 0.5 }}
          /> */}
          Parsed/Extracted Files
        </ToggleButton>
        {hiddenCount > 0 && (
          <ToggleButton value={LOAN_FILES_TABLE_LAYOUT_TYPES.HIDDEN}>
            <VisibilityOffOutlined
              className="thin"
              sx={{ fontSize: "16px", mr: 0.5 }}
            />
            Hidden Files ({hiddenCount})
          </ToggleButton>
        )}
      </ToggleButtonGroup>
    </Flx>
  );
};

const LoanFilesActionBanner = ({ selected, onCancel, actionType }) => {
  const dispatch = useDispatch();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { loading, parseAndExtractDocuments, sendBankStatementsToOcrulus } =
    useLoanFilesHook();
  const actionTypeObj = LOAN_FILE_ACTION_TYPES[actionType];

  const actionName = actionTypeObj?.name;
  // console.log("actionType", actionType);
  const handleOnSubmit = (d) => {
    if (
      actionName === LOAN_FILE_ACTION_TYPES.SEND_BANK_STATEMENTS_TO_OCRULUS.name
    ) {
      sendBankStatementsToOcrulus({
        dealId: loanDrilldown?._id,
        files: selected,
        onSuccessFn: () => {
          dispatch(loanFetchedDataParsedDocsClear());
          onCancel();
        },
      });
      // console.log("Sending bank statements to Oculus for:", selected);
      return;
    }

    if (
      actionName ===
      LOAN_FILE_ACTION_TYPES.PARSE_AND_EXTRACT_DATA_FROM_DOCUMENTS.name
    ) {
      parseAndExtractDocuments({
        dealId: loanDrilldown?._id,
        files: selected,
        onSuccessFn: () => {
          dispatch(loanFetchedDataParsedDocsClear());
          onCancel();
        },
      });
      // console.log("PARSAE DOCUMENTS:", selected);
      return;
    }
    // const mappedData = selected.map((row) => ({
    //   loanNumber: row.loanNumber,
    //   loanName: row.loanName,
    // }));

    // console.log("mappedData", mappedData);
    // return;
    // dispatch(workspaceTabsAddMany(mappedData));
    // onCancel();
  };
  return (
    <UnsavedChangesAlert
      onSubmit={handleOnSubmit}
      icon={actionTypeObj?.icon || <SaveOutlined />}
      onCancel={onCancel}
      loading={loading}
      text={`${actionTypeObj?.label} (${size(selected)})`}
      // subText={`${actionTypeObj?.label} (${size(selected)})`}
      subText="Select from the following documents."
      disabled={isEmpty(selected)}
      saveText={"Send"}
    />
  );
};

const ReportExpirationSection = ({ documents }) => {
  // 1. keep only docs that have a reportEffectiveDate
  const reportDocs = useMemo(
    () => documents.filter((doc) => doc.reportEffectiveDate && !doc?.isHidden),
    [documents],
  );
  if (isEmpty(reportDocs)) {
    return null;
  }
  return (
    <Flx column sx={{ mt: 1, mb: 3, p: 2 }}>
      {/* <Flx ac gap={0.8} sx={{ mb: 1, color: grey[900] }}>
        <AlarmOutlined className="thin" fontSize="small" />
        <Txt sx={{ fontWeight: 400, fontSize: "14px" }}>
          Time Sensitive Documents
        </Txt>
      </Flx> */}
      <SubsectionHeader
        title={`Time Sensitive Documents (${size(reportDocs)})`}
        icon={<AlarmOutlined className="thin" fontSize="small" />}
      />

      <ReportExpirationCards documents={documents} />
    </Flx>
  );
};

const ReportExpirationCards = ({ documents }) => {
  const dispatch = useDispatch();

  const reportDocs = useMemo(
    (file) => {
      return documents.filter(
        (doc) =>
          !isNil(doc.reportEffectiveDate) &&
          !isNil(doc.reportGuidelineExpirationDays),
      );
    },
    [documents],
  );

  const docsWithStatus = useMemo(
    () =>
      reportDocs?.map((doc) => {
        const effective = new Date(doc.reportEffectiveDate);
        const daysToExpire = Number(doc.reportGuidelineExpirationDays);
        const expiration = new Date(
          effective.getTime() + daysToExpire * 24 * 60 * 60 * 1000,
        );

        const now = Date.now();
        const effectiveMs = effective.getTime();
        const expirationMs = expiration.getTime();

        // total span and remaining span, in ms:
        const totalSpan = expirationMs - effectiveMs;
        const remainingSpan = Math.max(0, expirationMs - now);

        // percent remaining:
        const percentRemaining = (remainingSpan / totalSpan) * 100;

        // diffDays (for your label):
        const diffDays = Math.ceil(
          (expirationMs - now) / (1000 * 60 * 60 * 24),
        );

        let status;
        if (diffDays < 0) status = "expired";
        else if (diffDays <= 10) status = "expiringSoon";
        else status = "valid";

        return {
          ...doc,
          expiration,
          diffDays,
          status,
          percentRemaining,
        };
      }),
    [reportDocs],
  );

  const onPreviewClick = useCallback(
    (file) => {
      dispatch(
        sidebarSetValues({
          type: "filePreview",
          state: file,
        }),
      );
    },
    [dispatch],
  );

  // const relatedComments = getCommentsByDocumentId(loan, doc?._id);

  if (!docsWithStatus.length) {
    return <Txt>No reports with an effective date.</Txt>;
  }

  return (
    <Suspense fallback={<LoadingFallback />}>
      <FlxGrid width="340px" grow={size(docsWithStatus) > 3 ? 1 : 0} gap={2}>
        {docsWithStatus.map((doc) => {
          let chipColor, chipLabel, chipIcon, chipBackgroundColor;
          switch (doc.status) {
            case "valid":
              chipColor = green[700];
              chipBackgroundColor = "#ffffff";
              chipLabel = "Valid";
              chipIcon = (
                <CheckCircleOutline
                  // className="thin"
                  sx={{ color: green[700], fontSize: "15px", mr: 0.4 }}
                />
              );
              break;
            case "expiringSoon":
              chipColor = orange[700];
              chipBackgroundColor = orange[50];
              chipLabel = "Expiring Soon";
              chipIcon = (
                <WarningAmberRounded
                  // className="thin"
                  sx={{ color: orange[700], fontSize: "15px", mr: 0.4 }}
                />
              );
              break;
            case "expired":
              chipColor = red[700];
              chipBackgroundColor = red[50];
              chipLabel = "Expired";
              chipIcon = (
                <CancelRounded
                  // className="thin"
                  sx={{ color: red[700], fontSize: "15px", mr: 0.4 }}
                />
              );
              break;
            default:
              chipColor = "default";
          }

          return (
            <Card
              key={doc._id}
              sx={{
                // maxWidth: "680px",
                flexBasis: "340px",
                flexGrow: 1,
                flexShrink: 1,
              }}
            >
              <Flx column gap={3}>
                <Flx gap={1}>
                  <Suspense fallback={<LoadingFallback />}></Suspense>
                  <Flx column gap={0.4}>
                    <Txt sx={{ fontSize: "10px" }} secondary fontStyle="italic">
                      {doc.docType}
                    </Txt>

                    <Txt
                      onClick={() => onPreviewClick(doc)}
                      sx={{
                        cursor: "pointer",
                        fontWeight: 600,
                        fontSize: "13px",
                        color: grey[900],
                      }}
                    >
                      {doc.file_display_name}
                    </Txt>
                  </Flx>
                </Flx>
                <Flx ac gap={1}>
                  <Flx
                    fw
                    gap={0.5}
                    column
                    sx={{ ".txt": { fontSize: "12px" } }}
                  >
                    <Txt secondary>
                      {`Effective: ${new Date(
                        doc.reportEffectiveDate,
                      ).toLocaleDateString()}`}
                    </Txt>
                    <Txt secondary>
                      {`Expires: ${doc.expiration.toLocaleDateString()}`}
                    </Txt>
                  </Flx>
                  <Flx gap={0.4} ac>
                    <LoanFileCommentsBadge file={doc} />
                    <Flx
                      ac
                      sx={{
                        background: chipBackgroundColor,
                        py: 0.4,
                        pr: 0.8,
                        pl: 0.6,
                        borderRadius: 1,
                      }}
                    >
                      {chipIcon}
                      <Txt
                        sx={{
                          textWrap: "nowrap",
                          color: chipColor,
                          lineHeight: 1.2,
                          fontSize: "11px",
                          fontWeight: "bold",
                        }}
                      >
                        {doc.diffDays > 0
                          ? chipLabel
                          : doc.diffDays === 0
                            ? "Expires Today"
                            : "Expired"}
                      </Txt>
                    </Flx>

                    {doc.diffDays <= 0 ? null : (
                      <Flx column sx={{ position: "relative" }}>
                        <CircularProgress
                          variant="determinate"
                          value={doc.percentRemaining}
                          thickness={5}
                          sx={{
                            width: "50px !important",
                            height: "50px !important",
                            color: chipColor,
                            backgroundColor: "#f5f5f5", // Light grey background
                            borderRadius: "50%", // Ensures the background is circular
                          }}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            top: "25px",
                            left: "25px",
                            width: doc.diffDays <= 0 ? "50px" : "40px",
                            height: doc.diffDays <= 0 ? "50px" : "40px",
                            display: "flex",
                            alignItems: "center",
                            background: doc.diffDays <= 0 ? red[50] : "#ffffff",
                            justifyContent: "center",
                            borderRadius: "50%",
                            color: "inherit",
                            flexDirection: "column",
                            transform: "translate(-50%, -50%)",
                          }}
                        >
                          <Txt
                            sx={{
                              fontSize: "9px",
                              fontWeight: doc.diffDays <= 0 ? "bold" : "500",
                              color: chipColor,
                            }}
                          >
                            {doc.diffDays < 0 ? "Expired" : doc.diffDays}
                          </Txt>
                          {doc.diffDays < 0 ? null : (
                            <Txt
                              sx={{
                                fontSize: "9px",
                                lineHeight: 1,
                                color: chipColor,
                              }}
                            >
                              Days
                            </Txt>
                          )}
                        </Box>
                      </Flx>
                    )}
                  </Flx>
                </Flx>
              </Flx>
            </Card>
          );
        })}
      </FlxGrid>
      {/* </FlxGrid> */}
    </Suspense>
  );
};

const TableSortDropdrown = ({ selectedSort, onSortChange, sx = {} }) => {
  const options = useMemo(
    () => [
      {
        label: "Category & Type",
        icon: <TocOutlined />,
        value: {
          colId: "docGroup",
          sort: "asc",
        },
      },
      {
        label: "Upload Date (Newest)",
        icon: <UploadFileOutlined />,
        value: {
          colId: "uploadDate",
          sort: "desc",
        },
      },

      {
        label: "Upload Date (Oldest)",
        icon: <UploadFileOutlined />,
        value: {
          colId: "uploadDate",
          sort: "asc",
        },
      },

      {
        label: "Status",
        icon: <PublishedWithChangesOutlined />,
        value: {
          colId: "documentStatus",
          sort: "asc",
        },
      },
      {
        label: "Name (AZ)",
        icon: <SortByAlphaOutlined />,
        value: {
          colId: "file_display_name",
          sort: "asc",
        },
      },
      {
        label: "Name (ZA)",
        icon: <SwapVertOutlined sx={{ transform: "reflect(180)" }} />,
        value: {
          colId: "file_display_name",
          sort: "desc",
        },
      },
    ],
    [],
  );

  return (
    <TableSortDropdown
      options={options}
      value={selectedSort}
      allowDeselect
      onChange={onSortChange}
      buttonText="Sort By"
      sx={sx}
    />
  );
};

const DownloadLoanFilesAsZipButton = ({ icon }) => {
  const { loading, downloadLoanDocs } = useLoanFilesHook();

  const loanDrilldown = useSelector((state) => state.loanDrilldown);

  const loanId = loanDrilldown?._id;
  const loanNumber = loanDrilldown?.loanNumber;
  const onDownloadClick = useCallback(() => {
    downloadLoanDocs({
      loanId: loanId,
      fileDownloadName: `Loan ${loanNumber} Files`,
    });
  }, [downloadLoanDocs, loanId, loanNumber]);

  return (
    <Tooltip title="Download Loan Files as ZIP">
      <IconButton
        color="primary"
        loading={loading}
        // size="small"
        sx={{ p: 0.5 }}
        onClick={onDownloadClick}
      >
        <DownloadOutlined
          sx={{ fontSize: "24px !important" }}
          className="thin"
        />
      </IconButton>
    </Tooltip>
  );
  // if (icon) {
  //   return (
  //     <Tooltip title="Download Loan Files as ZIP">
  //       <IconButton
  //         color="primary"
  //         loading={loading}
  //         size="small"
  //         onClick={onDownloadClick}
  //       >
  //         <DownloadOutlined className="thin" />
  //       </IconButton>
  //     </Tooltip>
  //   );
  // }
  // return (
  //   <Button
  //     size="medium"
  //     loading={loading}
  //     variant="outlined"
  //     // variant="contained"
  //     onClick={onDownloadClick}
  //     startIcon={<DownloadOutlined className="thin" />}
  //   >
  //     Download
  //   </Button>
  // );
};

LoanDrilldownFilesTab.propTypes = {
  value: PropTypes.any,
  tab: PropTypes.any,
};

LoanFilesTableContent.propTypes = {
  files: PropTypes.array,
  hiddenCount: PropTypes.number,
  layoutMode: PropTypes.string,
  setLayoutMode: PropTypes.func,
};

FileTableCardHeader.propTypes = {
  quickFilter: PropTypes.string,
  setQuickFilter: PropTypes.func,
  layoutMode: PropTypes.string,
  onLayoutChange: PropTypes.func,
  onUpdateClick: PropTypes.func,
  loading: PropTypes.bool,
  changedRowsCount: PropTypes.number,
  hiddenCount: PropTypes.number,
  editing: PropTypes.bool,
  onEditCancel: PropTypes.func,
  onEditClick: PropTypes.func,
  changedRows: PropTypes.array,
};

LayoutToggle.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  editing: PropTypes.bool,
  hiddenCount: PropTypes.number,
};

ReportExpirationSection.propTypes = {
  documents: PropTypes.array,
};

ReportExpirationCards.propTypes = {
  documents: PropTypes.array,
};

DownloadLoanFilesAsZipButton.propTypes = {
  loanId: PropTypes.string,
  loanNumber: PropTypes.string,
};

export default LoanDrilldownFilesTab;
